import { AtmosphericDataEnum } from "@bienesis/common";
import { ArrowDown, ArrowUp, Droplets, Sun, Thermometer, Wind } from "lucide-react";
import { DateTime } from "luxon";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import "twin.macro";

import {
  AtmosphericData,
  ClimateDataGranularity,
} from "@/api/climate-data/types";
import {
  Card,
  ClimateRangeControls,
  SmallStatCard,
  useClimateRange,
} from "./range-selector";
import {
  AtmosphericDataLoader,
  useAtmosphericDataContext,
} from "../data-loaders/atmospheric-data-loader";
import {
  ProtectionPeriod,
  ProtectionPeriodComponent,
  ProtectionPeriodList,
} from "./protection-period-list.component";
import {
  ProtectionLegendItem,
  formatChartTick,
  renderProtectionAreas,
} from "./protection-chart.component";

type AtmosphericType = AtmosphericDataEnum;

const VARIABLE_CONFIG: Record<
  AtmosphericType,
  { color: string; iconBg: string; labelKey: string; Icon: typeof Sun }
> = {
  [AtmosphericDataEnum.TEMPERATURE]: {
    color: "#F87171",
    iconBg: "#FEE2E2",
    labelKey: "meteo.climateHistory.temperatures",
    Icon: Thermometer,
  },
  [AtmosphericDataEnum.IRRADIANCE]: {
    color: "#F59E0B",
    iconBg: "#FEF3C7",
    labelKey: "meteo.climateHistory.solarIrradiance",
    Icon: Sun,
  },
  [AtmosphericDataEnum.HUMIDITY]: {
    color: "#22D3EE",
    iconBg: "#CFFAFE",
    labelKey: "meteo.climateHistory.humidity",
    Icon: Droplets,
  },
  [AtmosphericDataEnum.WIND]: {
    color: "#64748B",
    iconBg: "#F1F5F9",
    labelKey: "meteo.climateHistory.wind",
    Icon: Wind,
  },
};

const formatValue = (value: number | null, unit: string) =>
  value === null
    ? "—"
    : `${value.toLocaleString("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      })}${unit ? ` ${unit}` : ""}`;

type Aggregates = {
  min: number | null;
  mean: number | null;
  max: number | null;
};

const computeAggregates = (series: AtmosphericData["series"]): Aggregates => {
  const mins = series.map((p) => p.min).filter((v): v is number => v !== null);
  const maxs = series.map((p) => p.max).filter((v): v is number => v !== null);
  const means = series
    .map((p) => p.mean)
    .filter((v): v is number => v !== null);
  return {
    min: mins.length ? Math.min(...mins) : null,
    max: maxs.length ? Math.max(...maxs) : null,
    mean: means.length ? means.reduce((a, b) => a + b, 0) / means.length : null,
  };
};

const COLOR_MIN = "#3B82F6";
const COLOR_MEAN = "#F59E0B";
const COLOR_MAX = "#EF4444";

type ChartPoint = {
  ts: number;
  min: number | null;
  mean: number | null;
  max: number | null;
};

const buildChartData = (series: AtmosphericData["series"]): ChartPoint[] =>
  series.map((point) => ({
    ts: DateTime.fromISO(point.timestamp).toMillis(),
    min: point.min,
    mean: point.mean,
    max: point.max,
  }));

const AtmosphericChart = ({
  series,
  protectedPeriods,
  granularity,
}: {
  series: AtmosphericData["series"];
  protectedPeriods: AtmosphericData["protectedPeriods"];
  granularity: ClimateDataGranularity;
}) => {
  const { t } = useTranslation();
  const chartData = useMemo(() => buildChartData(series), [series]);
  const unit = series.find((p) => p.unit)?.unit ?? "";
  const isHourly = granularity === "hour";

  const formatTick = (ts: number) => formatChartTick(ts, granularity);

  return (
    <Card tw="p-4 flex flex-col gap-4">
      <div tw="flex items-baseline gap-2">
        <p tw="text-base font-semibold text-gray-800">
          {t("meteo.atmosphericView.chart.title")}
        </p>
        {unit && <span tw="text-xs text-gray-400">({unit})</span>}
      </div>

      <div tw="w-full h-56">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 8, left: -16, bottom: 8 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#E5E7EB"
            />
            {renderProtectionAreas(protectedPeriods)}
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              tickFormatter={formatTick}
              tick={{ fill: "#6B7280", fontSize: 10 }}
              stroke="#9CA3AF"
              tickLine={false}
              axisLine={false}
              height={30}
            />
            <YAxis
              domain={["auto", "auto"]}
              tick={{ fill: "#6B7280", fontSize: 10 }}
              stroke="#9CA3AF"
              tickLine={false}
              axisLine={false}
            />
            {!isHourly && (
              <Line
                type="monotone"
                dataKey="min"
                stroke={COLOR_MIN}
                strokeWidth={2}
                dot={{ r: 2, fill: COLOR_MIN, strokeWidth: 0 }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
                connectNulls
              />
            )}
            {!isHourly && (
              <Line
                type="monotone"
                dataKey="mean"
                stroke={COLOR_MEAN}
                strokeWidth={2.5}
                dot={{ r: 2.5, fill: COLOR_MEAN, strokeWidth: 0 }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
                connectNulls
              />
            )}
            <Line
              type="monotone"
              dataKey="max"
              stroke={COLOR_MAX}
              strokeWidth={2.5}
              dot={{ r: 2.5, fill: COLOR_MAX, strokeWidth: 0 }}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div tw="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600 pt-2 border-t border-gray-100">
        {!isHourly && (
          <span tw="flex items-center gap-2">
            <span
              tw="inline-block w-4 h-0.5 rounded-full"
              style={{ backgroundColor: COLOR_MIN }}
            />
            {t("meteo.atmosphericView.stats.min")}
          </span>
        )}
        {!isHourly && (
          <span tw="flex items-center gap-2">
            <span
              tw="inline-block w-4 h-0.5 rounded-full"
              style={{ backgroundColor: COLOR_MEAN }}
            />
            {t("meteo.atmosphericView.stats.mean")}
          </span>
        )}
        <span tw="flex items-center gap-2">
          <span
            tw="inline-block w-4 h-0.5 rounded-full"
            style={{ backgroundColor: COLOR_MAX }}
          />
          {t("meteo.atmosphericView.stats.max")}
        </span>
        <ProtectionLegendItem />
      </div>
    </Card>
  );
};

const AtmosphericProtectionPeriodList = ({
  periods,
}: {
  periods: ProtectionPeriod[];
  granularity: ClimateDataGranularity;
}) => {
  return (
    <ProtectionPeriodList periods={periods}>
      {periods.map((p, i) => (
        <ProtectionPeriodComponent
          key={`${p.from}-${i}`}
          from={p.from}
          to={p.to}
          isLast={i === periods.length - 1}
        />
      ))}
    </ProtectionPeriodList>
  );
};

function AtmosphericContent({
  config,
  granularity,
}: {
  config: (typeof VARIABLE_CONFIG)[AtmosphericType];
  granularity: ClimateDataGranularity;
}) {
  const { t } = useTranslation();
  const { data, isFetching } = useAtmosphericDataContext();

  const aggregates = useMemo(
    () => computeAggregates(data.series),
    [data.series]
  );
  const unit = data.series.find((p) => p.unit)?.unit ?? "";

  return (
    <Fragment>
      <Card tw="flex items-center gap-3 px-4 py-3">
        <div
          tw="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: config.iconBg }}
        >
          <config.Icon tw="w-6 h-6" style={{ color: config.color }} />
        </div>
        <div tw="flex flex-col">
          <p tw="text-xs text-gray-500">
            {t("meteo.atmosphericView.stats.mean")}
          </p>
          <p tw="text-3xl font-bold" style={{ color: config.color }}>
            {formatValue(aggregates.mean, unit)}
          </p>
        </div>
      </Card>

      <div tw="flex gap-3">
        <SmallStatCard
          icon={<ArrowDown tw="w-5 h-5 text-sky-600" />}
          iconBg="#E0F2FE"
          label={t("meteo.atmosphericView.stats.min")}
          value={formatValue(aggregates.min, unit)}
          valueColor="#0284C7"
        />
        <SmallStatCard
          icon={<ArrowUp tw="w-5 h-5 text-orange-600" />}
          iconBg="#FFEDD5"
          label={t("meteo.atmosphericView.stats.max")}
          value={formatValue(aggregates.max, unit)}
          valueColor="#EA580C"
        />
      </div>

      <AtmosphericChart
        series={data.series}
        protectedPeriods={data.protectedPeriods}
        granularity={granularity}
      />

      <AtmosphericProtectionPeriodList
        periods={data.protectedPeriods}
        granularity={granularity}
      />

      {isFetching && (
        <p tw="text-xs text-gray-400 text-center">
          {t("meteo.climateView.refreshing")}
        </p>
      )}
    </Fragment>
  );
}

export default function AtmosphericDataViewSheetBody({
  areaId,
  type,
}: {
  areaId: string;
  type: AtmosphericType;
}) {
  const range = useClimateRange();
  const { appliedRange } = range;
  const config = VARIABLE_CONFIG[type];

  return (
    <div tw="flex flex-col gap-4 px-4 pb-6">
      <ClimateRangeControls range={range} />
      <AtmosphericDataLoader
        areaId={areaId}
        type={type}
        from={appliedRange.from}
        to={appliedRange.to}
        granularity={appliedRange.granularity}
      >
        <AtmosphericContent
          config={config}
          granularity={appliedRange.granularity}
        />
      </AtmosphericDataLoader>
    </div>
  );
}
